import { useEffect, useRef, useState } from 'react'

// Konfigurasi gelembung (statis biar tidak ada hydration mismatch)
const BUBBLES = [
  { left: '18%', size: 10, delay: 1.6, dur: 4.5 },
  { left: '30%', size: 6, delay: 2.1, dur: 3.8 },
  { left: '42%', size: 14, delay: 1.9, dur: 5.2 },
  { left: '55%', size: 8, delay: 2.4, dur: 4.2 },
  { left: '63%', size: 5, delay: 1.7, dur: 3.5 },
  { left: '72%', size: 11, delay: 2.2, dur: 4.8 },
  { left: '84%', size: 7, delay: 2.6, dur: 4.0 },
  { left: '48%', size: 4, delay: 2.8, dur: 3.4 },
]

const RAYS = [
  { left: '12%', rotate: -12 },
  { left: '40%', rotate: -4, delay: 1.5 },
  { left: '68%', rotate: 8, delay: 0.8 },
  { left: '86%', rotate: 14, delay: 2.2 },
]

export function SeaIntro({ onDone, onExit }: { onDone: () => void; onExit?: () => void }) {
  const [exiting, setExiting] = useState(false)
  const doneRef = useRef(false)
  const finishRef = useRef<() => void>(() => {})

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const total = reduce ? 1100 : 3200
    const exitMs = 650

    const finish = () => {
      if (doneRef.current) return
      doneRef.current = true
      setExiting(true)
      onExit?.()
      window.setTimeout(onDone, exitMs)
    }
    finishRef.current = finish

    // Selesai otomatis, ATAU bisa di-skip (klik / ESC / Enter / Space).
    const t = window.setTimeout(finish, total)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') finish()
    }
    window.addEventListener('keydown', onKey)
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', onKey) }
  }, [onDone, onExit])

  return (
    <div
      className={`sea-intro ${exiting ? 'sea-intro--exit' : ''}`}
      onClick={() => finishRef.current()}
      role="button"
      tabIndex={-1}
      aria-label="Lewati intro"
      style={{ cursor: 'pointer' }}
    >
      {/* Tombol lewati */}
      <button
        onClick={(e) => { e.stopPropagation(); finishRef.current() }}
        className="absolute top-5 right-5 z-10 px-3 py-1.5 rounded-full text-[11px] font-body font-semibold text-white/80 bg-white/10 border border-white/20 hover:bg-white/20 hover:text-white backdrop-blur-sm transition-colors"
      >
        Lewati →
      </button>
      {/* God rays */}
      {RAYS.map((r, i) => (
        <div key={i} className="sea-intro__ray"
          style={{ left: r.left, transform: `rotate(${r.rotate}deg)`, animationDelay: `${r.delay ?? 0}s` }} />
      ))}

      {/* Permukaan air + splash */}
      <div className="sea-intro__surface" />
      <div className="sea-intro__splash" />
      <div className="sea-intro__splash sea-intro__splash--2" />

      {/* Gelembung */}
      {BUBBLES.map((b, i) => (
        <span key={i} className="sea-intro__bubble"
          style={{ left: b.left, width: b.size, height: b.size, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s` }} />
      ))}

      {/* Jangkar turun */}
      <div className="sea-intro__anchor">
        <div className="sea-intro__chain" />
        <div className="sea-intro__anchor-sway">
          <svg viewBox="0 0 140 190" fill="none" className="w-full h-auto">
            <g stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
              {/* ring */}
              <circle cx="70" cy="20" r="11" />
              {/* shank */}
              <line x1="70" y1="31" x2="70" y2="151" />
              {/* stock / crossbar */}
              <line x1="44" y1="52" x2="96" y2="52" />
              {/* arms */}
              <path d="M28 120 C 34 158 54 170 70 151 C 86 170 106 158 112 120" />
            </g>
            {/* flukes (solid) */}
            <path d="M28 120 L 14 116 L 24 132 Z" fill="currentColor" />
            <path d="M112 120 L 126 116 L 116 132 Z" fill="currentColor" />
          </svg>
        </div>
      </div>

      {/* Vignette kedalaman */}
      <div className="sea-intro__vignette" />

      {/* Teks brand */}
      <div className="sea-intro__text">
        <p className="sea-intro__eyebrow text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#BfE0D6] font-body mb-3">
          Holding
        </p>
        <h1 className="sea-intro__title font-display text-3xl sm:text-5xl font-bold leading-none">
          Jangkar Arunika Luminara
        </h1>
        <div className="sea-intro__sub mt-5 flex items-center justify-center gap-2 text-[#AFD4CC] font-body text-xs sm:text-sm">
          <span>Memasuki sistem</span>
          <span className="flex gap-1">
            <span className="sea-intro__dot w-1 h-1 rounded-full bg-current inline-block" />
            <span className="sea-intro__dot w-1 h-1 rounded-full bg-current inline-block" />
            <span className="sea-intro__dot w-1 h-1 rounded-full bg-current inline-block" />
          </span>
        </div>
      </div>
    </div>
  )
}
